import { AdminOnlyRouteNotice } from "@circle-react-shared/uikit/HeaderV3/NavigationBar/AdminOnlyRouteNotice";
import { LazyKeyboardShortcutsSideEffects } from "@circle-react-shared/uikit/KeyboardShortcuts/LazyKeyboardShortcutsSideEffects";
import { useIsReplitOnboardingModalEligible } from "@circle-react/components/CircleAi/ReplitOnboardingModal/useIsReplitOnboardingModalEligible";
import { GDPRCookieBanner } from "@circle-react/components/GDPR/GDPRCookieBanner";
import { CircleAIAppShellLayout } from "@circle-react/components/Layout/CircleAIAppShellLayout";
import { ShellBypassProvider } from "@circle-react/components/Layout/CircleAIAppShellLayout/ShellBypassContext";
import { PostModalOverlay } from "@circle-react/components/PostModal/PostModalOverlay";
import { lazyWithRetry } from "@circle-react/helpers/lazyRetry";
import { applyV3Fonts } from "@circle-react/providers/Theme";
import { Outlet } from "@tanstack/react-router";
import { Suspense } from "react";
import { useRenderActivationConfirmationModal } from "react/components/NewVersionOptIn/ActivationConfirmationModal/hooks/useRenderActivationConfirmationModal";
import { ActivationLoadingScreen } from "react/components/NewVersionOptIn/ActivationLoadingScreen";
import { useRenderAdminPreviewModal } from "react/components/NewVersionOptIn/AdminPreviewModal/hooks/useRenderAdminPreviewModal";
import { useRenderCircleAIIntroModal } from "react/components/NewVersionOptIn/CircleAIIntro/hooks/useRenderCircleAIIntroModal";
import { useRenderEclipseReadyModal } from "react/components/NewVersionOptIn/EclipseReadyModal/useRenderEclipseReadyModal";
import { useRenderVersionActivationModal } from "react/components/NewVersionOptIn/VersionActivationModal/hooks/useRenderVersionActivationModal";

import { useIsMemberAreaAppShellEnabled } from "@/react/hooks/useIsMemberAreaAppShellEnabled";
import { usePendoGuideModalShow } from "@/react/hooks/usePendoGuideModalShow";

import { LegacyMainAppContent } from "./LegacyMainAppContent";
import type { MainAppContentProps } from "./types";
import { useIsMinimalLayoutRoute } from "./useIsMinimalLayoutRoute";

const ReplitOnboardingModal = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "ReplitOnboardingModal" */ "@circle-react/components/CircleAi/ReplitOnboardingModal"
  ).then(module => ({ default: module.ReplitOnboardingModal })),
);

export const MainAppContent = (props: MainAppContentProps) => {
  const isMemberAreaAppShellEnabled = useIsMemberAreaAppShellEnabled();
  const isMinimalLayoutRoute = useIsMinimalLayoutRoute();
  const isReplitOnboardingModalEligible = useIsReplitOnboardingModalEligible();
  applyV3Fonts();
  useRenderAdminPreviewModal();
  useRenderActivationConfirmationModal();
  useRenderVersionActivationModal();
  useRenderCircleAIIntroModal();
  useRenderEclipseReadyModal();

  usePendoGuideModalShow();

  // Mount once for all shell paths (legacy, app shell, and `_minimal`) so
  // shortcuts register even when the header UI is hidden.
  const keyboardShortcuts = <LazyKeyboardShortcutsSideEffects />;

  // Legacy shell path. The `_minimal` route bypass below only runs on the new
  // AppShell; here full-screen routes (e.g. the Live room) instead rely on the
  // backend sending `hasMinimalLayout: true` to suppress the header/chrome, as
  // they did before this segment existed.
  if (!isMemberAreaAppShellEnabled) {
    return (
      <>
        {keyboardShortcuts}
        <LegacyMainAppContent {...props} />
      </>
    );
  }

  // Route-level layout selection. See `tanstack_routes/_minimal/route.tsx` for
  // the rationale. Note: this `_minimal` *segment* is distinct from the
  // backend-driven `hasMinimalLayout` prop below.
  if (isMinimalLayoutRoute) {
    return (
      <>
        {keyboardShortcuts}
        <Outlet />
      </>
    );
  }

  const { hasMinimalLayout, shouldDisplayHeader } = props;

  return (
    <ShellBypassProvider>
      {keyboardShortcuts}
      <CircleAIAppShellLayout
        hasMinimalLayout={hasMinimalLayout}
        shouldDisplayHeader={shouldDisplayHeader}
      >
        <Outlet />
      </CircleAIAppShellLayout>
      <PostModalOverlay />
      <GDPRCookieBanner />
      <AdminOnlyRouteNotice />
      {/* Global mount: the /ai activation target redirects into the panel, so
          the screen has to outlive the route it used to hang off. */}
      <ActivationLoadingScreen />
      {isReplitOnboardingModalEligible && (
        <Suspense fallback={null}>
          <ReplitOnboardingModal />
        </Suspense>
      )}
    </ShellBypassProvider>
  );
};
